import { hasError, type Result } from "operation-result.js";
import { SubtreeController } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import { SignInCancelled, type SignInFailed, signInWithEmail, signInWithGoogle } from "../../core/ops/auth-ops";
import { hasCredentialErrors, validateCredentials } from "../../core/primitives/credentials";
import { LoginActions, LoginState } from "./login-model";

export type LoginDeps = Pick<AppDeps, "session">;

/**
 * No routing: the session notifies when it opens, the app root's gate flips to
 * `signedIn`, and the route component swaps this page for the app.
 */
export class LoginController extends SubtreeController implements LoginActions {
  readonly state = new LoginState();
  #submitted = false;

  constructor(readonly deps: LoginDeps) {
    super();
    this.subtree.put(LoginState, this.state);
    this.subtree.put(LoginActions, this);
  }

  setEmail = (value: string) => {
    this.state.draft.update((d) => ({ ...d, email: value }));
    this.#revalidate();
  };

  setPassword = (value: string) => {
    this.state.draft.update((d) => ({ ...d, password: value }));
    this.#revalidate();
  };

  submit = async () => {
    this.#submitted = true;
    const draft = this.state.draft.value;
    const errors = validateCredentials(draft);
    this.state.fieldErrors.value = errors;
    if (hasCredentialErrors(errors)) return;
    await this.#attempt(() =>
      signInWithEmail({ ...this.deps, email: draft.email.trim(), password: draft.password.trim() }),
    );
  };

  continueWithGoogle = () => this.#attempt(() => signInWithGoogle(this.deps));

  // const sendMagicLink = () =>
  //   this.#attempt(async () => {
  //     const email = this.state.draft.value.email.trim();
  //     if (!email) return fail(new SignInFailed("Enter your email first, then request a magic link."));
  //     const r = await sendMagicLink({ ...this.deps, email });
  //     if (r.ok) this.state.info.value = "Check your inbox for a sign-in link.";
  //     return r;
  //   });

  #revalidate(): void {
    if (this.#submitted) this.state.fieldErrors.value = validateCredentials(this.state.draft.value);
  }

  async #attempt(fn: () => Promise<Result<void, SignInFailed | SignInCancelled>>): Promise<void> {
    if (this.state.busy.value) return;
    this.state.busy.value = true;
    this.state.error.value = null;
    this.state.info.value = null;
    try {
      const r = await fn();
      if (this.isDisposed) return;
      if (!r.ok && !hasError(r, SignInCancelled)) this.state.error.value = r.errors[0].message;
    } finally {
      this.state.busy.value = false;
    }
  }
}
